import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import { IconButton, ListItem, ListItemButton, Menu, Typography } from "@mui/material";
import type { ReactElement } from "react";
import { useCallback, useState } from "react";
import { MenuItemStyled } from "@entrilia/shared/components/MenuItemStyled";

import type { ReportField } from "../../../../../../../../api/types/svTypes.ts";
import { isAggregation } from "../../../../utilities/fieldsHelper.ts";
import { getDisplayCaption } from "../../../viewer/common/fields/field-captions/captionsHelper.ts";
import FieldDimensionCaption from "../../../viewer/common/fields/field-captions/FieldDimensionCaption.tsx";

interface Props {
  onAddField: (field: ReportField) => void;
  availableFields: ReportField[];
  disabled: boolean;
}

const SelectFieldInput = ({ onAddField, availableFields, disabled }: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);

  const onBeforeClose = useCallback(() => {
    setOpen(false);
  }, []);

  const addFieldToGroup = useCallback(
    (field: ReportField) => {
      onAddField(field);
      onBeforeClose();
    },
    [onAddField, onBeforeClose]
  );

  return (
    <>
      <ListItem
        role="Handle"
        ref={(ref) => setAnchorEl(ref)}
        sx={(theme) => ({
          display: "flex",
          border: `1px solid ${theme.palette.text.disabled}`,
          borderRadius: "2px",
          p: "5px",
          backgroundColor: theme.palette.background.paper,
          height: "32px",
        })}
      >
        <ListItemButton
          disabled={availableFields.length === 0 || disabled}
          onClick={() => {
            if (availableFields.length > 0) {
              setOpen(true);
            }
          }}
          sx={{ "&:hover": { bgcolor: "transparent" }, p: 0 }}
          disableTouchRipple
          disableRipple
        >
          <Typography variant="caption" sx={{ flexGrow: 1, color: "text.primary" }}>
            Select Field
          </Typography>
          <IconButton>
            <ArrowDropDownRoundedIcon fontSize="medium" />
          </IconButton>
        </ListItemButton>
      </ListItem>
      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={onBeforeClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              boxShadow: "0px 3px 4px rgba(0, 0, 0, 0.14), 0px 1px 8px rgba(0, 0, 0, 0.12)",
              width: "322px",
              maxWidth: "400px",
              maxHeight: "350px",
            },
          },
        }}
      >
        {availableFields.map((field) => {
          let caption: string | ReactElement = getDisplayCaption(field.meta.caption, field.config.customLabel);
          if (isAggregation(field)) {
            caption = <FieldDimensionCaption field={field} />;
          }
          return (
            <MenuItemStyled key={field.config.guid} onClick={() => addFieldToGroup(field)}>
              <Typography variant="caption" noWrap>
                {caption}
              </Typography>
            </MenuItemStyled>
          );
        })}
      </Menu>
    </>
  );
};

export default SelectFieldInput;
